import { motion } from "framer-motion";

// Stylised Gulf region -> India corridor: several origin nodes arc into one India node.
export default function CorridorMap({ className = "" }) {
  const gulf = [
    { x: 70, y: 150, label: "UAE" },
    { x: 48, y: 215, label: "KSA" },
    { x: 120, y: 105, label: "Qatar" },
    { x: 100, y: 268, label: "Oman" },
  ];
  const india = { x: 470, y: 300, label: "India" };
  const accent = "#1447E6";

  return (
    <svg
      viewBox="0 0 560 460"
      className={className}
      role="img"
      aria-label="Abstract corridor map linking the Gulf region and India"
      data-testid="corridor-map"
    >
      <defs>
        <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.55" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* abstract dotted field */}
      {Array.from({ length: 11 }).map((_, row) =>
        Array.from({ length: 14 }).map((_, col) => {
          const x = 40 + col * 38 + (row % 2 ? 19 : 0);
          const y = 40 + row * 38;
          const inCorridor = x > 60 && x < 500 && y > 80 && y < 340;
          return (
            <circle
              key={`${row}-${col}`}
              cx={x}
              cy={y}
              r={inCorridor ? 1.8 : 1.2}
              fill="currentColor"
              opacity={inCorridor ? 0.28 : 0.12}
            />
          );
        })
      )}

      {/* corridor lines from each Gulf node into India */}
      {gulf.map((g, i) => {
        const midX = (g.x + india.x) / 2;
        const midY = Math.min(g.y, india.y) - 60 - i * 8;
        const path = `M ${g.x} ${g.y} Q ${midX} ${midY}, ${india.x} ${india.y}`;
        return (
          <g key={g.label}>
            <motion.path
              d={path}
              fill="none"
              stroke={accent}
              strokeWidth="1.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2.2, ease: "easeInOut", delay: 0.3 + i * 0.3 }}
            />
            <motion.circle
              r="3"
              fill={accent}
              animate={{ offsetDistance: ["0%", "100%"] }}
              transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}
              style={{ offsetPath: `path('${path}')` }}
            />
          </g>
        );
      })}

      {/* Gulf nodes */}
      {gulf.map((g, i) => (
        <g key={`node-${g.label}`}>
          <circle cx={g.x} cy={g.y} r="22" fill="url(#nodeGlow)" />
          <motion.circle cx={g.x} cy={g.y} r="4.5" fill={accent} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + i * 0.3 }} />
          <text x={g.x} y={g.y - 16} textAnchor="middle" className="fill-current text-[11px] font-semibold uppercase tracking-[0.15em]">
            {g.label}
          </text>
        </g>
      ))}

      {/* India node */}
      <circle cx={india.x} cy={india.y} r="32" fill="url(#nodeGlow)" />
      <motion.circle cx={india.x} cy={india.y} r="6" fill={accent} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2 }} />
      <text x={india.x} y={india.y + 36} textAnchor="middle" className="fill-current text-[13px] font-semibold uppercase tracking-[0.2em]">
        {india.label}
      </text>
    </svg>
  );
}
